// 2D orbit visual: seven validation layers orbiting the RegO AI core.
import React, { useEffect, useRef, useState } from "react";
import {
  VALIDATION_LAYERS,
  readPalette,
  type PipelineRun,
} from "./processingPipeline";
import {
  LAYER_RUN_STATUS,
  ORBIT_BOX,
  ORBIT_RADIUS,
  PARTICLE_COUNT,
  PARTICLE_CLEARANCE,
  TONE_PARTICLES,
  type ConsoleTone,
} from "./constants";
import { RegoShieldMark } from "./RegoShieldMark";
import type {
  LayerKey,
  LayerRunState,
} from "../../../stores/processingJobStore";

export function PipelineOrbit2D({
  run,
  paused = false,
  settled = false,
  tone = "neutral",
  active = true,
  layerRuns,
}: {
  run: PipelineRun;
  paused?: boolean;
  settled?: boolean;
  tone?: ConsoleTone;
  /** When false, freezes canvas particles AND all CSS animations so the orbit
   *  only spins while data processing is happening. */
  active?: boolean;
  /** Live per-layer classification run state; drives per-icon lit/failed/active
   *  status so the orbit updates as each layer finishes, not only at the end. */
  layerRuns?: Partial<Record<LayerKey, LayerRunState>>;
}) {
  const canvasPaused = paused || !active;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      const room = Math.min(width, height) - 24;
      const next = Math.max(0.45, Math.min(1, room / ORBIT_BOX));
      scaleRef.current = next;
      setScale(next);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (canvasPaused) return;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let raf = 0;
    let colors = readPalette().chart;
    let parts: {
      a: number;
      rad: number;
      r: number;
      sp: number;
      ph: number;
      color: string;
    }[] = [];

    const seed = () => {
      colors =
        tone === "neutral"
          ? readPalette().chart
          : (TONE_PARTICLES[tone] as unknown as typeof colors);
      const rect = wrap.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width));
      canvas.height = Math.max(1, Math.floor(rect.height));

      const maxRad = Math.hypot(canvas.width, canvas.height) / 2;
      const minRad = Math.min(
        PARTICLE_CLEARANCE * scaleRef.current,
        maxRad * 0.7,
      );
      const spread = Math.max(40, maxRad - minRad);

      /** Cryptographically secure replacement for Math.random() – satisfies SonarQube S2245. */
      const secureRandom = (): number => {
        const buf = new Uint32Array(1);
        crypto.getRandomValues(buf);
        return buf[0] / (0xffffffff + 1);
      };

      parts = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        a: secureRandom() * Math.PI * 2,
        rad: minRad + secureRandom() * spread,
        r: 0.6 + secureRandom() * 1.6,
        sp: 0.15 + secureRandom() * 0.4,
        ph: secureRandom() * Math.PI * 2,
        color: colors[i % colors.length],
      }));
    };

    seed();
    const ro = new ResizeObserver(seed);
    ro.observe(wrap);
    const themeObserver = new MutationObserver(seed);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const frame = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      for (const p of parts) {
        if (!reduced) p.a += p.sp * 0.004;
        const x = cx + Math.cos(p.a) * p.rad;
        const y = cy + Math.sin(p.a) * p.rad;
        const tw = 0.5 + 0.5 * Math.sin((t / 700) * p.sp * 4 + p.ph);
        ctx.beginPath();
        ctx.globalAlpha = 0.2 + 0.4 * tw;
        ctx.fillStyle = p.color;
        ctx.arc(x, y, p.r * (0.6 + 0.5 * tw), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      themeObserver.disconnect();
    };
  }, [canvasPaused, tone]);

  return (
    <div ref={wrapRef} className="relative grid h-full w-full place-items-center">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      <div
        className={`relative ${active ? "" : "[&_*]:![animation-play-state:paused]"}`}
        style={{
          width: ORBIT_BOX,
          height: ORBIT_BOX,
          transform: `scale(${scale})`,
        }}
      >
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-border/70"
          style={{
            width: ORBIT_RADIUS * 2,
            height: ORBIT_RADIUS * 2,
            animation: "rgc-spin-rev 48s linear infinite",
          }}
        />

        {VALIDATION_LAYERS.map((s, i) => {
          const angle =
            (-90 + i * (360 / VALIDATION_LAYERS.length)) * (Math.PI / 180);
          const layerRun = layerRuns?.[s.key as LayerKey];
          const runComplete = layerRun?.status === LAYER_RUN_STATUS.COMPLETE;
          const runRunning = layerRun?.status === LAYER_RUN_STATUS.RUNNING;
          const runError = layerRun?.status === LAYER_RUN_STATUS.ERROR;
          const lit = runComplete || i < run.layersLit;
          const isActive = runRunning
            ? true
            : !run.done && run.layersLit > 0 && i === run.layersLit;
          // Only red the layer when the run actually errored — a layer that was
          // never reached (no layerRun entry) after settle should read as neutral,
          // not as a failure.
          const failed = runError || (settled && !!layerRun && !lit && !isActive);
          const Icon = s.icon;
          return (
            <div key={s.key}>
              <span
                className="absolute left-1/2 top-1/2 z-[1] h-px origin-left transition-opacity duration-500"
                style={{
                  width: ORBIT_RADIUS,
                  transform: `rotate(${angle}rad)`,
                  opacity: lit ? 0.75 : 0.15,
                  background: lit
                    ? "linear-gradient(90deg, #10b981, transparent)"
                    : "var(--border)",
                }}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-1/2 size-1.5 rounded-full"
                    style={
                      {
                        background: s.accent,
                        boxShadow: `0 0 8px ${s.accent}`,
                        "--rgc-reach": `${ORBIT_RADIUS - 20}px`,
                        animation: "rgc-travel 1.6s ease-in-out infinite",
                      } as React.CSSProperties
                    }
                  />
                )}
              </span>
              <div
                className="absolute z-[2] w-24 -translate-x-1/2 -translate-y-1/2 text-center"
                style={{
                  left: ORBIT_BOX / 2 + Math.cos(angle) * ORBIT_RADIUS,
                  top: ORBIT_BOX / 2 + Math.sin(angle) * ORBIT_RADIUS,
                  animation: "rgc-drift 4.5s ease-in-out infinite",
                  animationDelay: `${i * 0.55}s`,
                }}
              >
                <span
                  className={`relative mx-auto grid size-10 place-items-center rounded-xl border transition-all duration-500 ${
                    isActive
                      ? "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-300"
                      : lit
                        ? "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300"
                        : failed
                          ? "border-red-200 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-400"
                          : "border-border bg-card text-[#94a3b8] dark:text-white/30"
                  }`}
                  style={{
                    transform: isActive ? "scale(1.12)" : "none",
                  }}
                >
                  <Icon className="size-[18px]" />
                  {isActive && (
                    <span
                      className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-indigo-400/70"
                      style={{ animation: "rgc-ring 1.5s ease-out infinite" }}
                    />
                  )}
                </span>
                <span
                  className={`mt-1.5 block whitespace-nowrap text-[10px] transition-colors duration-500 ${
                    lit ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </div>
          );
        })}

        <div className="pointer-events-none absolute left-1/2 top-1/2 z-[3] size-24 -translate-x-1/2 -translate-y-1/2">
          {[0, 1, 2].map((n) => (
            <span
              key={n}
              className="absolute inset-0 rounded-full border border-indigo-400/45"
              style={{
                animation: "rgc-pulse-out 3.6s ease-out infinite",
                animationDelay: `${n * 1.2}s`,
              }}
            />
          ))}
        </div>

        <span
          className="pointer-events-none absolute left-1/2 top-1/2 z-[3] size-32 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-md"
          style={{
            background:
              "conic-gradient(from 0deg, transparent, var(--chart-1), transparent 45%, var(--chart-2), transparent)",
            animation: "rgc-spin 9s linear infinite",
          }}
        />

        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-[4] grid size-24 -translate-x-1/2 -translate-y-1/2 place-items-center"
          style={{ animation: "rgc-breathe 3.4s ease-in-out infinite" }}
        >
          <RegoShieldMark size={80} />
        </div>
      </div>
    </div>
  );
}
